/* eslint-disable no-underscore-dangle */
/* eslint-disable @typescript-eslint/naming-convention */
import { OptionBase } from 'chakra-react-select'
import { format, parseISO } from 'date-fns'

import { type EventFormOptions } from '@hooks/useEventFormOptions'

import {
    EnrichedMatchEditorApiEvent,
    EventFormData,
    MatchEventEditorApiEvent,
    MatchEventEditorEventType,
    MatchEventEditorFixtureExtraInfo,
    MatchEventEditorGoalExtraInfo,
    MatchTime,
} from '@common/types'

export type Option = OptionBase & {
    value?: string
    label?: string
}

export type CreatableOption = Option & {
    __isNew__?: boolean
}

export function makePrettierLabel(label: string) {
    const words = label.split('_')
    const prettierLabel = words.map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')

    return prettierLabel
}

export function getMatchEditorFixtureStatusLabel(extraInfo: number) {
    return makePrettierLabel(
        Object.entries(MatchEventEditorFixtureExtraInfo)
            .find(([_, v]) => Number(v) === extraInfo)?.[0]
            .toString() ?? '',
    )
}

export function getMatchEditorEventTypeLabel(key: string | number) {
    let eventType = key

    if (typeof key === 'string') {
        eventType = MatchEventEditorEventType[key as keyof typeof MatchEventEditorEventType]
    }

    const eventTypeLabel = Object.keys(MatchEventEditorEventType).find(
        k => MatchEventEditorEventType[k as keyof typeof MatchEventEditorEventType] === eventType,
    )

    const customLabels = {
        video_assistant_referee: 'VAR Check',
        video_assistant_referee_over: 'VAR Over',
        fixture_status: 'Period Event',
        penalty_shootout_event_deep_coverage: 'Penalty Missed (in shootout)',
    }

    if (eventTypeLabel) {
        if (customLabels[eventTypeLabel] !== undefined) {
            return customLabels[eventTypeLabel]
        }

        return makePrettierLabel(eventTypeLabel)
    }

    return '-'
}

export type FilterOptionOption<OptionType> = Option & {
    data: OptionType
}

export function playerSearchFilter(option: FilterOptionOption<Option>, searchText: string) {
    const { label, value } = option.data

    if (label?.length === 0 && value?.length === 0) {
        return false
    }

    return !!(
        label?.toLowerCase().includes(searchText.toLowerCase()) ||
        value?.toLowerCase().includes(searchText.toLowerCase())
    )
}

export function formatMatchTime(matchTime: MatchTime, isAdditionalTimeSelected: boolean) {
    const { minutes, seconds, additionalMinutes } = matchTime

    const isAddedTime = [45, 90, 105, 120].includes(minutes)
    const formattedMinutes = Number(minutes).toString().padStart(2, '0')
    let formattedSeconds = Number(seconds).toString().padStart(2, '0')
    let formattedAdditionalMinutes = ''

    if (isAddedTime) {
        formattedSeconds = '00'

        if (isAdditionalTimeSelected && additionalMinutes?.length && Number(additionalMinutes) > 0) {
            formattedAdditionalMinutes = ` +${Number(additionalMinutes).toString().padStart(2, '0')}:${Number(seconds)
                .toString()
                .padStart(2, '0')}`
        }
    }

    const result = `${formattedMinutes}:${formattedSeconds}${formattedAdditionalMinutes}`

    return result
}

export function getPlayerIdFromOption(option?: Option | CreatableOption) {
    if (!option) {
        return null
    }

    if ((option as CreatableOption)?.__isNew__ || option.value?.includes('is:fake:player:')) {
        return null
    }

    return option.value?.length ? option.value : null
}

export function getPlayerNameFromOption(option?: Option | CreatableOption) {
    if (!option) {
        return null
    }

    // If we're reusing fake player, we're sending both name and ID, thus parsing by 'fake',
    // as returned fake IDs look like 'fake:player:-576460752303411742
    if ((option as CreatableOption)?.__isNew__ || option.value?.includes('fake')) {
        return option.label?.length ? option.label : null
    }

    return null
}

export function getOptionFromId(id: string, options: any[]) {
    const playerOption = options.find(option => option.value === id)

    if (!id || !playerOption) {
        return {
            value: '',
            label: 'Unknown (no id)',
        }
    }

    return playerOption
}

export function extractPlayersFromEvents(teamId: string, events: EnrichedMatchEditorApiEvent[]) {
    const playersMap = events.reduce((accumulator, event) => {
        if (event.team_id !== teamId) {
            return accumulator
        }

        if (event.player1_id && event.player1_id.includes('fake:player')) {
            const player1 = {
                value: event.player1_id,
                label: event?.player1_name ?? event.player1_id,
            }

            const existingPlayer = accumulator.get(player1.value)
            if (!existingPlayer || (existingPlayer && player1.label !== player1.value)) {
                accumulator.set(player1.value, player1)
            }
        }

        if (event.player2_id && event.player2_id.includes('fake:player')) {
            const player2 = {
                value: event.player2_id,
                label: event?.player2_name ?? event.player2_id,
            }

            const existingPlayer = accumulator.get(player2.value)
            if (!existingPlayer || (existingPlayer && player2.label !== player2.value)) {
                accumulator.set(player2.value, player2)
            }
        }

        return accumulator
    }, new Map<string, Option>())

    return Array.from(playersMap.values())
}

export function createOptions(obj: any, labelMaker: (key: string) => string) {
    return Object.keys(obj).map(key => ({
        value: String(obj[key as keyof typeof obj]),
        label: labelMaker(key),
    }))
}

export function findOption(value: string, options: any[]) {
    return options.find(option => String(option.value) === value)
}

export function getEventFormPreservedValues(formData: EventFormData): Partial<EventFormData> {
    return {
        team_id: formData.team_id,
        type: formData.type,
        time: formData.time,
        player1_id: formData.player1_id,
        player2_id: formData.player2_id,
        team1_score: formData.team1_score,
        team2_score: formData.team2_score,
        extra_info: formData.extra_info,
        period_number: formData.period_number,
        review_original_decision: formData.review_original_decision,
        review_outcome: formData.review_outcome,
        penalty_order: formData.penalty_order,
    }
}

export function mapEventToFormData(
    selectedEvent: MatchEventEditorApiEvent,
    options: EventFormOptions,
): {
    formData: EventFormData
    isAdditionalTimeSelected: boolean
} {
    const timeParts = selectedEvent?.event_time?.split('+') ?? ['00:00']
    const [matchTime, additionalTime] = timeParts

    const [matchMinutes, matchSeconds] = matchTime.split(':').map(Number)

    let additionalMinutes = 0
    let seconds = matchSeconds
    let isAdditionalTimeSelected = false

    if (additionalTime) {
        const [extraMinutes, extraSeconds] = additionalTime.split(':').map(Number)
        additionalMinutes = extraMinutes

        seconds = extraSeconds

        isAdditionalTimeSelected = true
    }

    const periodNumber = selectedEvent?.period_number
        ? findOption(String(selectedEvent.period_number), options.periods)
        : options.periods[0]

    const type = findOption(String(selectedEvent.type), options.types)
    let formData: EventFormData = {
        type,
        time: {
            minutes: matchMinutes,
            seconds,
            additionalMinutes: String(additionalMinutes),
        },
        timestamp:
            selectedEvent.timestamp ?? format(parseISO(selectedEvent.timestamp ?? new Date().toISOString()), 'T'),

        team1_score: Number(selectedEvent.team1_score || selectedEvent.score?.team1 || 0),
        team2_score: Number(selectedEvent.team2_score || selectedEvent.score?.team2 || 0),

        extra_info: selectedEvent.extra_info,
        period_number: periodNumber,
        penalty_order: selectedEvent.penalty_order,
    }

    // Fill players and team for every event that has these fields
    if (Number(type.value) !== MatchEventEditorEventType.fixture_status) {
        formData = {
            ...formData,
            team_id: findOption(String(selectedEvent.team_id), options.teams),
            player1_id: getOptionFromId(selectedEvent.player1_id, options.players[selectedEvent.team_id] ?? []),
            player2_id: getOptionFromId(selectedEvent.player2_id, options.players[selectedEvent.team_id] ?? []),
        }
    }

    // Fill review details for VAR over event
    if (Number(type.value) === MatchEventEditorEventType.video_assistant_referee_over) {
        formData = {
            ...formData,
            review_original_decision: getOptionFromId(
                String(selectedEvent.review_details.original_decision),
                options.varOverReview,
            ),
            review_outcome: getOptionFromId(String(selectedEvent.review_details.outcome), options.varOverReview),
        }
    }

    // Use players from the other team for own goal event
    if (
        Number(type.value) === MatchEventEditorEventType.goal &&
        Number(selectedEvent.extra_info) === MatchEventEditorGoalExtraInfo.own_goal
    ) {
        const unselectedTeamId = options.teams.find(team => team.value !== String(selectedEvent.team_id))?.value ?? ''
        formData = {
            ...formData,
            player1_id: getOptionFromId(selectedEvent.player1_id, options.players[unselectedTeamId] ?? []),
            player2_id: getOptionFromId(selectedEvent.player2_id, options.players[unselectedTeamId] ?? []),
        }
    }

    return { formData, isAdditionalTimeSelected }
}

function parseMatchTime(matchTime: string) {
    let totalSeconds = 0
    if (matchTime.includes('+')) {
        const [mainTime, extraTime] = matchTime.split('+')
        const [mainMinutes, mainSeconds] = mainTime.split(':').map(Number)
        const [extraMinutes, extraSeconds] = extraTime.split(':').map(Number)
        totalSeconds = mainMinutes * 60 + mainSeconds + (extraMinutes * 60 + extraSeconds)
    } else {
        const [minutes, seconds] = matchTime.split(':').map(Number)
        totalSeconds = minutes * 60 + seconds
    }

    return totalSeconds
}

export function matchEventSortFn(a: EnrichedMatchEditorApiEvent, b: EnrichedMatchEditorApiEvent) {
    if (a.period_number !== b.period_number) {
        return b.period_number - a.period_number
    }

    return parseMatchTime(b.event_time) - parseMatchTime(a.event_time)
}
